import {
  ChangeDetectorRef,
  Directive,
  EmbeddedViewRef,
  inject,
  NgZone,
  OnDestroy,
  output,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { CustomFormControlComponent } from '@shared/components/editor/custom-form-control/custom-form-control.component';
import { createPopper, Instance } from '@popperjs/core';
import { fromEvent, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/** Inheritance-only base for popper-anchored dropdowns. `V` is the form value type. */
@Directive()
export abstract class PopupComponent<V = unknown>
  extends CustomFormControlComponent<V>
  implements OnDestroy
{
  readonly opened = output();

  readonly closed = output();

  protected cdr = inject(ChangeDetectorRef);

  protected view?: EmbeddedViewRef<unknown>;

  protected yOffset = 0;

  protected popperRef?: Instance;

  /** Guards the focus-opens-popup trigger while focus is restored after an Escape close. */
  protected suppressFocusOpen = false;

  private vcr = inject(ViewContainerRef);

  private zone = inject(NgZone);

  private readonly closeSignal$ = new Subject<void>();

  get isOpen(): boolean {
    return !!this.popperRef;
  }

  open(dropdownTpl: TemplateRef<unknown>, origin: HTMLElement): void {
    if (this.popperRef) {
      return;
    }
    this.view = this.vcr.createEmbeddedView(dropdownTpl);
    const popup = this.view?.rootNodes[0] as HTMLElement;

    document.body.appendChild(popup);
    popup.style.width = `${origin.offsetWidth}px`;

    this.zone.runOutsideAngular(() => {
      this.popperRef = createPopper(origin, popup, {
        placement: 'bottom-end',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, this.yOffset],
            },
          },
        ],
      });
    });
    this.handleClickOutside();
    this.handleEscape();
    this.opened.emit();
  }

  /** Focus-triggered open — no-op while focus is being restored after an Escape close. */
  openFromFocus(dropdownTpl: TemplateRef<unknown>, origin: HTMLElement): void {
    if (this.suppressFocusOpen) {
      return;
    }
    this.open(dropdownTpl, origin);
  }

  close(): void {
    this.closeSignal$.next();
    this.closed.emit();
    this.popperRef?.destroy();
    this.view?.destroy();
    this.view = undefined;
    this.popperRef = undefined;
  }

  ngOnDestroy(): void {
    this.close();
    this.closeSignal$.complete();
  }

  writeValue(_obj: V | null): void {}

  private closeAndRestoreFocus(): void {
    const origin = this.popperRef?.state.elements.reference as HTMLElement | undefined;
    this.close();
    const focusTarget = origin?.matches('button, [tabindex]')
      ? origin
      : origin?.querySelector<HTMLElement>('button, input, [tabindex]');
    if (focusTarget) {
      this.suppressFocusOpen = true;
      focusTarget.focus();
      setTimeout(() => (this.suppressFocusOpen = false));
    }
  }

  private handleEscape(): void {
    this.zone.runOutsideAngular(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(
          filter((event) => event.key === 'Escape'),
          takeUntil(this.closeSignal$),
        )
        .subscribe(() => {
          this.zone.run(() => {
            this.closeAndRestoreFocus();
            this.cdr.detectChanges();
          });
        });
    });
  }

  private handleClickOutside(): void {
    fromEvent(document, 'click')
      .pipe(
        filter(({ target }) => {
          const origin = this.popperRef?.state.elements.reference as HTMLElement;
          return !origin?.contains(target as Node);
        }),
        takeUntil(this.closeSignal$),
      )
      .subscribe((event) => {
        const popup = this.popperRef?.state.elements.popper;
        const target = event.target instanceof Element ? event.target : null;
        // closest()/contains() instead of className string checks — SVG targets have an
        // SVGAnimatedString className that would crash a string comparison.
        if (popup?.contains(target) || target?.closest('.mat-calendar')) {
          return;
        }
        this.close();
        this.cdr.detectChanges();
      });
  }
}
